import { MAP_FONTS_TO_CHALK } from 'utils/constants';

import React, { useMemo } from 'react';

import { ChalkThemeProvider } from '@thinkific/chalk/react-providers';
import { getThemedFontFamilyName, getThemedPalette } from '@thinkific/chalk/themes-utils';

import { useCommunityContext } from 'components/CommunityContext';

type ThemeContextType = ReturnType<typeof getThemedPalette> & {
  font: string;
};

export const defaultThemeContext: ThemeContextType = {
  action: {
    primary: {
      lightest: '',
      light: '',
      base: '',
      dark: '',
      darkest: '',
      safeOnLight: '',
      safeOnDark: '',
    },
  },
  onAction: {
    primary: {
      lightest: '',
      light: '',
      base: '',
      dark: '',
      darkest: '',
      safeOnLight: '',
      safeOnDark: '',
    },
  },
  onLight: {
    border: {
      actionPrimaryFallback: '',
    },
  },
  onDark: {
    border: {
      actionPrimaryFallback: '',
    },
  },
  font: '',
};

export const ThemeContext = React.createContext<ThemeContextType>(defaultThemeContext);

export const useTheme = () => React.useContext(ThemeContext);

export const CommunityThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { community } = useCommunityContext();

  const palette = useMemo(() => {
    if (!community) return {};
    return getThemedPalette(community?.primaryColor);
  }, [community?.primaryColor]);

  if (!community) return children;

  return (
    <ThemeContext.Provider
      value={
        {
          ...palette,
          font: getThemedFontFamilyName(MAP_FONTS_TO_CHALK[community?.font] || 'sourceSans'),
        } as ThemeContextType
      }
    >
      <ChalkThemeProvider
        bodyFont={MAP_FONTS_TO_CHALK[community.font]}
        primaryColor={community.primaryColor}
        titleFont={MAP_FONTS_TO_CHALK[community.font]}
      >
        {children}
      </ChalkThemeProvider>
    </ThemeContext.Provider>
  );
};

export default CommunityThemeProvider;
